import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import Pagination from "../components/Pagination";
import PageHeader from "../components/PageHeader";

const empty = { name: "", address: "", postal_code: "", city: "", nif: "", contact: "", email: "" };

export default function Clientes() {
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/clients", { params: { page, q } }).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao guardar"); }
  };

  const confirmDel = async () => {
    try { await api.delete(`/clients/${delTarget.id}`); toast.success("Cliente eliminado"); setDelTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao eliminar"); }
  };

  return (
    <div data-testid="clientes-page">
      <PageHeader title="Clientes" subtitle="Gestão de clientes da agência" testid="clientes-header"
        actions={<Button onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="add-cliente-btn"><Plus size={16} /> Novo Cliente</Button>} />

      <div className="a4w-card p-2 mb-4 flex items-center gap-2">
        <Search size={18} className="ml-2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (page === 1 ? load() : setPage(1))}
          placeholder="Pesquisar por nome, NIF ou email…" className="border-0 focus-visible:ring-0 shadow-none" data-testid="clientes-search" />
        <Button variant="outline" onClick={() => (page === 1 ? load() : setPage(1))} data-testid="clientes-search-btn">Pesquisar</Button>
      </div>

      <div className="a4w-card overflow-hidden">
        <table className="w-full text-sm" data-testid="clientes-table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-3 a4w-label">Nome</th>
              <th className="px-4 py-3 a4w-label">NIF</th>
              <th className="px-4 py-3 a4w-label">Cidade</th>
              <th className="px-4 py-3 a4w-label">Contacto</th>
              <th className="px-4 py-3 a4w-label">Email</th>
              <th className="px-4 py-3 a4w-label">Criado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(c => (
              <tr key={c.id} className="border-t border-slate-100 a4w-table-row">
                <td className="px-4 py-3 font-medium text-slate-800">{c.name || "—"}</td>
                <td className="px-4 py-3">{c.nif || "—"}</td>
                <td className="px-4 py-3">{c.city || "—"}</td>
                <td className="px-4 py-3">{c.contact || "—"}</td>
                <td className="px-4 py-3">{c.email || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-cliente-${c.id}`}><Pencil size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setDelTarget(c)} data-testid={`del-cliente-${c.id}`}><Trash2 size={16} className="text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Sem clientes ainda. Clique em "Novo Cliente".</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="cliente-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
            <DialogDescription>Todos os campos são opcionais.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cliente-name" /></div>
            <div className="col-span-2"><Label>Morada</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cliente-address" /></div>
            <div><Label>Código Postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="cliente-cp" /></div>
            <div><Label>Cidade</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="cliente-city" /></div>
            <div><Label>NIF</Label><Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} data-testid="cliente-nif" /></div>
            <div><Label>Contacto</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="cliente-contact" /></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cliente-email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cliente-cancel">Cancelar</Button>
            <Button onClick={save} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="cliente-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação elimina também os serviços e notas associadas. Não pode ser revertida.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDel} className="bg-red-600 hover:bg-red-700" data-testid="cliente-del-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
